import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { environment } from '@environment';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { OnboardingService } from '../onboarding.service';
import { OnboardingAnchorDirective } from '../onboarding-anchor.directive';

/**
 * Дверь в библиотеку уроков — один кружок 32 px на все шапки: и кабинет, и редакторы рисуют его
 * отсюда, поэтому рамка и подпись у них не разъезжаются. Кому дверь положена, считает
 * хост; здесь только вид и одно нажатие — окно уроков открывается сразу, без промежуточного меню (R16).
 * Рядом — кнопка повтора подсказок экрана, живая только вне прода (R29b).
 */
@Component({
  selector: 'onboarding-lessons-door',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [HlmButton, HlmTooltip, IconComponent, OnboardingAnchorDirective],
  template: `
    <button
      hlmBtn
      type="button"
      variant="ghost"
      [size]="size() === 'lg' ? 'icon-lg' : 'icon-sm'"
      class="relative rounded-full border
        pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5 pointer-coarse:after:content-['']"
      [class]="size() === 'lg' ? 'border-line3 bg-transparent text-c2' : 'border-line2 bg-panel2'"
      hlmTooltip="Видео-обзоры"
      aria-label="Видео-обзоры"
      [onboardingAnchor]="anchor()"
      (click)="open.emit()"
    >
      <app-icon name="video" [size]="size() === 'lg' ? 18 : 16" />
    </button>

    @if (canReplay && replay() && onboarding.screenHintCount() > 0) {
      <button
        hlmBtn
        type="button"
        variant="ghost"
        size="icon-sm"
        class="rounded-full text-c3 pointer-coarse:relative
          pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5 pointer-coarse:after:content-['']"
        hlmTooltip="Показать подсказки этой страницы"
        aria-label="Показать подсказки этой страницы"
        (click)="onboarding.replayScreen()"
      >
        <app-icon name="rotate-ccw" [size]="16" />
      </button>
    }
  `,
})
export class LessonsDoorComponent {
  protected readonly onboarding = inject(OnboardingService);

  /**
   * Повтор подсказок — инструмент дева и стейджа: держит его тот же флаг, что разрешает прогон
   * онбординга по адресу, и на проде он выключен. Значение статично — читаем один раз при сборке.
   */
  protected readonly canReplay = environment.allowOnboardingReplay;

  /** Якорь коуча — только там, куда коуч приходит (шапка кабинета). */
  readonly anchor = input<string | null>(null);
  /** `lg` — кружок 40 px с прозрачной заливкой и рамкой `--line3` для обвязки модального мастера на фоне страницы. */
  readonly size = input<'sm' | 'lg'>('sm');
  /** Повтор подсказок нужен только там, где на экране живут подсказки коуча. */
  readonly replay = input(true);
  readonly open = output<void>();
}
